import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { updatePageSEO } from '../../utils/seo';

export default function Calendar() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    updatePageSEO({
      title: 'Book Consultation | Curtis Waters AI Solutions Charlotte NC',
      description: 'Schedule your AI consultation with Curtis Waters in Charlotte NC. Book a free deal evaluation session. Expert business intelligence analysis, 25+ years experience. Get instant insights for your business decisions.',
      keywords: 'book consultation, schedule meeting, AI consultation Charlotte, deal evaluation appointment, business intelligence consultation, Curtis Waters booking, free consultation',
      canonical: `${import.meta.env.VITE_SITE_URL}/calendar`,
      ogTitle: 'Book Consultation | Curtis Waters AI Solutions Charlotte NC',
      ogDescription: 'Schedule your AI consultation with Curtis Waters in Charlotte NC. Book a free deal evaluation session. Expert business intelligence analysis, 25+ years experience. Get instant insights for your business decisions.',
      ogUrl: `${import.meta.env.VITE_SITE_URL}/calendar`,
    });

    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://curtiswaters.ai';

    // Track page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Schedule Consultation',
        page_location: window.location.href,
        page_path: '/calendar'
      });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'calendar_page_view',
        page_type: 'booking'
      });
    }

    // Set page title
    document.title = 'Schedule Consultation - Curtis Waters AI';

    // Set meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    setMetaTag('description', 'Schedule a free AI business intelligence consultation with Curtis Waters. Book your appointment for expert deal evaluation and customized AI solutions.');
    setMetaTag('keywords', 'schedule consultation, AI consultation, Curtis Waters calendar, business intelligence appointment, deal evaluation');
    setMetaTag('robots', 'noindex, nofollow');
    setMetaTag('og:title', 'Schedule Consultation - Curtis Waters AI', true);
    setMetaTag('og:description', 'Book your free AI business intelligence consultation with Curtis Waters', true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', `${siteUrl}/calendar`, true);
    setMetaTag('twitter:card', 'summary');
    setMetaTag('twitter:title', 'Schedule Consultation - Curtis Waters AI');
    setMetaTag('twitter:description', 'Book your free AI business intelligence consultation');

    // Set canonical URL
    let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', `${siteUrl}/calendar`);

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Schedule Consultation - Curtis Waters AI",
      "description": "Schedule a free AI business intelligence consultation with Curtis Waters.",
      "url": `${siteUrl}/calendar`
    };

    const existingScript = document.querySelector('script[data-schema="calendar"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'calendar');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="calendar"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateAvailable = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today && date.getDay() !== 0 && date.getDay() !== 6;
  };

  const handleDateSelect = (day: number) => {
    if (isDateAvailable(day)) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      setSelectedDate(date);
      setSelectedTime('');
      
      // Track date selection
      if (window.gtag) {
        window.gtag('event', 'select_date', {
          event_category: 'Calendar',
          event_label: date.toLocaleDateString(),
          value: 1
        });
      }
      
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'calendar_date_selected',
          selected_date: date.toISOString()
        });
      }
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    
    // Track time selection
    if (window.gtag) {
      window.gtag('event', 'select_time', {
        event_category: 'Calendar',
        event_label: time,
        value: 1
      });
    }
    
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'calendar_time_selected',
        selected_time: time,
        selected_date: selectedDate?.toISOString()
      });
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track appointment confirmation
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL',
        event_category: 'Appointment',
        event_label: 'Consultation Booked',
        value: 1
      });
      
      window.gtag('event', 'generate_lead', {
        event_category: 'Lead',
        event_label: 'Calendar Booking',
        value: 1
      });
    }
    
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'appointment_confirmed',
        appointment_date: selectedDate?.toISOString(),
        appointment_time: selectedTime,
        conversion_type: 'consultation_booking'
      });
    }
    
    // Show success message or redirect
    alert('Appointment confirmed! You will receive a confirmation email shortly.');
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Schedule Your Free Consultation
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Book a convenient time for your <strong>AI business intelligence assessment</strong> with Curtis Waters. We'll discuss your <strong>deal evaluation needs</strong> and provide customized <strong>AI solutions</strong> for your business.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Select a Date</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={previousMonth}
                    className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    aria-label="Previous month"
                  >
                    <i className="ri-arrow-left-s-line text-xl text-gray-700"></i>
                  </button>
                  <span className="text-lg font-medium text-gray-900 min-w-[140px] text-center">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>
                  <button
                    onClick={nextMonth}
                    className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    aria-label="Next month"
                  >
                    <i className="ri-arrow-right-s-line text-xl text-gray-700"></i>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                    {day}
                  </div>
                ))}
                
                {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                  <div key={`empty-${index}`} className="aspect-square"></div>
                ))}
                
                {Array.from({ length: daysInMonth }).map((_, index) => {
                  const day = index + 1;
                  const available = isDateAvailable(day);
                  const isSelected = selectedDate?.getDate() === day &&
                    selectedDate?.getMonth() === currentMonth.getMonth() &&
                    selectedDate?.getFullYear() === currentMonth.getFullYear();

                  return (
                    <button
                      key={day}
                      onClick={() => handleDateSelect(day)}
                      disabled={!available}
                      className={`aspect-square rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-teal-500 text-white'
                          : available
                          ? 'bg-gray-50 text-gray-900 hover:bg-teal-50 hover:text-teal-600'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <i className="ri-information-line text-blue-500 mr-2"></i>
                  Available Monday - Friday. Weekends are not available for consultations.
                </p>
              </div>
            </div>

            {/* Time Slots & Booking Form */}
            <div className="space-y-6">
              {selectedDate && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Available Times for {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {timeSlots.map(time => (
                      <button
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        className={`py-3 px-4 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          selectedTime === time
                            ? 'bg-teal-500 text-white'
                            : 'bg-gray-50 text-gray-900 hover:bg-teal-50 hover:text-teal-600'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedDate && selectedTime && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Your Details</h2>
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="(704) 555-1234"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                      <textarea
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                        placeholder="Tell us about your AI business intelligence needs..."
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Confirm Appointment
                    </button>
                  </form>
                </div>
              )}

              {!selectedDate && (
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <i className="ri-calendar-line text-5xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600">Select a date to view available time slots</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
